import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { IconArrowRight, IconGlobe, IconHandshake, IconLightbulb, IconAward, IconUsers, IconHeart } from "../components/Icons";

const values = [
  {
    icon: IconLightbulb,
    title: "Innovation",
    desc: "Continuously pushing the boundaries of simulation technology and behaviour analysis to deliver next-generation solutions.",
  },
  {
    icon: IconAward,
    title: "Quality",
    desc: "Every product is engineered with meticulous attention to anatomical accuracy, durability, and performance standards.",
  },
  {
    icon: IconHeart,
    title: "Ethics",
    desc: "Committed to reducing reliance on live animals in training by providing realistic, ethical alternatives for education.",
  },
  {
    icon: IconGlobe,
    title: "Global Reach",
    desc: "Our solutions help advance veterinary education, surgical skills, and animal research internationally.",
  },
  {
    icon: IconHandshake,
    title: "Partnership",
    desc: "Working closely with educators and researchers to develop solutions that address real-world training challenges.",
  },
  {
    icon: IconUsers,
    title: "Community",
    desc: "Building a global network of veterinary professionals dedicated to advancing animal health and welfare.",
  },
];

const milestones = [
  { year: "2021", title: "Founded", desc: "Haptone was established in Turkey with TÜBİTAK support, aiming to revolutionize veterinary education and animal behavioural research." },
  { year: "2022", title: "First Product", desc: "Launched our first behaviour analysis software for rodents and dogs, using advanced computer vision." },
  { year: "2023", title: "Home Cage Monitoring", desc: "Introduced a 24/7 rodent home cage monitoring system for continuous behavioural tracking." },
  { year: "2024", title: "Surgical Training", desc: "Released surgical training products, including laparoscopy and suturing pads, under the Surgical Skills line." },
  { year: "2025", title: "Udder Models", desc: "Launched our first anatomical and silicone udder models for milking and handling training." },
  { year: "2026", title: "Auscultation Training", desc: "Launched realistic canine auscultation simulators, providing hands-on training for heart and lung examination." },
];

export default function About() {
  return (
    <>
      <SEO
        title="About Haptone"
        description="Learn about Haptone's mission to advance veterinary education and animal behaviour research through innovative simulation models and analysis systems."
        canonical="https://haptone.com/about"
      />

      <section className="relative pt-32 pb-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url(/images/about-bread.jpg)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/60 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-accent-light text-sm font-semibold uppercase tracking-widest">
            About Us
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl">
            Practice with Purpose, Research with Precision
          </h1>
          <p className="mt-6 text-white/80 text-lg sm:text-xl leading-relaxed max-w-2xl">
            We are dedicated to empowering students, educators, and researchers
            with tools that make learning and experimentation more accessible,
            reliable, and impactful.
          </p>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-accent font-semibold text-sm uppercase tracking-widest">
                Our Mission
              </span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-primary leading-tight">
                Mastering Veterinary Practice, Driving Animal Research
              </h2>
              <p className="mt-6 text-muted text-lg leading-relaxed">
                At Haptone, we empower veterinary students with realistic
                simulation models, support clinicians in refining surgical skills
                safely, and provide researchers with precise platforms to study
                animal behaviour.
              </p>
              <p className="mt-4 text-muted text-lg leading-relaxed">
                Founded by veterinary professionals, engineers, and researchers,
                we combine expertise with innovative technology to deliver
                ethical, reliable, and impactful solutions that advance
                veterinary education, surgical training, and animal research.
              </p>
              <p className="mt-4 text-muted text-lg leading-relaxed">
                Haptone operates through Haptone Technology Inc. (Turkey) for
                domestic training programs, and Haptone Educational
                Technologies Research &amp; Development FZCO (UAE) for
                international clients.
              </p>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src="/images/ABOUT US 4 LÜ/Gemini_Generated_Image_cehgaqcehgaqcehg.jpg"
                      alt="Animal behaviour tracking"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-auto"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src="/images/ABOUT US 4 LÜ/Gemini_Generated_Image_u1ujyku1ujyku1uj.jpg"
                      alt="Rodent behaviour"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src="/images/ABOUT US 4 LÜ/Gemini_Generated_Image_9b5y009b5y009b5y.jpg"
                      alt="Surgical training"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-auto"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src="/images/ABOUT US 4 LÜ/Gemini_Generated_Image_hf67duhf67duhf67.jpg"
                      alt="Veterinary model"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-accent font-semibold text-sm uppercase tracking-widest">
              What Drives Us
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-primary leading-tight">
              Our Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-2xl p-8 hover:shadow-xl hover:shadow-secondary/10 transition-all duration-500 hover:-translate-y-1 group"
              >
                <div className="w-14 h-14 bg-accent/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-accent group-hover:text-white transition-all duration-300">
                  <item.icon
                    size={24}
                    className="text-accent group-hover:text-white transition-colors duration-300"
                  />
                </div>
                <h3 className="text-xl font-bold text-primary mb-3">
                  {item.title}
                </h3>
                <p className="text-muted leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-accent font-semibold text-sm uppercase tracking-widest">
              Our Journey
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-primary leading-tight">
              Milestones That Define Us
            </h2>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-border" />

            <div className="space-y-12 lg:space-y-0">
              {milestones.map((item, i) => (
                <div
                  key={item.year}
                  className={`relative lg:flex lg:items-center lg:mb-16 last:lg:mb-0 ${i % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                    }`}
                >
                  <div
                    className={`lg:w-1/2 ${i % 2 === 0 ? "lg:pr-16 lg:text-right" : "lg:pl-16"
                      }`}
                  >
                    <div className="bg-light rounded-2xl p-6 lg:p-8 hover:shadow-lg transition-shadow duration-300">
                      <span className="text-secondary font-bold text-2xl">
                        {item.year}
                      </span>
                      <h3 className="mt-2 text-xl font-bold text-primary">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-muted leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>

                  <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-secondary rounded-full border-4 border-white shadow-md" />

                  <div className="lg:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-light overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10 text-center">
          <span className="text-accent font-semibold text-sm uppercase tracking-widest">References</span>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-primary">Trusted by Institutions</h2>
        </div>

        <div className="relative w-full">
          <div className="absolute left-0 top-0 h-full w-24 bg-gradient-to-r from-light to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 h-full w-24 bg-gradient-to-l from-light to-transparent z-10 pointer-events-none" />

          <div className="flex animate-scroll-left whitespace-nowrap">
            {[
              { name: "University 1", logo: "/images/logolar/uni1.png" },
              { name: "University 2", logo: "/images/logolar/uni2.png" },
              { name: "University 3", logo: "/images/logolar/uni3.png" },
              { name: "University 4", logo: "/images/logolar/uni4.png" },
              { name: "University 5", logo: "/images/logolar/uni5.png" },
              { name: "University 6", logo: "/images/logolar/uni6.png" },
              { name: "University 7", logo: "/images/logolar/uni7.png" },
              { name: "University 8", logo: "/images/logolar/uni8.png" },
              { name: "University 9", logo: "/images/logolar/uni9.png" },
            ].concat([
              { name: "University 1", logo: "/images/logolar/uni1.png" },
              { name: "University 2", logo: "/images/logolar/uni2.png" },
              { name: "University 3", logo: "/images/logolar/uni3.png" },
              { name: "University 4", logo: "/images/logolar/uni4.png" },
              { name: "University 5", logo: "/images/logolar/uni5.png" },
              { name: "University 6", logo: "/images/logolar/uni6.png" },
              { name: "University 7", logo: "/images/logolar/uni7.png" },
              { name: "University 8", logo: "/images/logolar/uni8.png" },
              { name: "University 9", logo: "/images/logolar/uni9.png" },
            ]).map((uni, i) => (
              <div key={i} className="inline-flex items-center justify-center mx-10 flex-shrink-0">
                <img
                  src={uni.logo}
                  alt={uni.name}
                  className="h-14 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300 opacity-60 hover:opacity-100"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="relative py-24 lg:py-32 overflow-hidden"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1920&h=800&fit=crop)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-secondary/90 to-accent/90" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            Interested in Working With Us?
          </h2>
          <p className="mt-6 text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">
            Whether you are an educator, researcher, or distributor, we would
            love to hear from you and explore how we can collaborate.
          </p>
          <div className="mt-10">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-primary font-semibold px-8 py-4 rounded-xl hover:bg-gray-100 transition-all duration-300 hover:shadow-xl"
            >
              Get in Touch
              <IconArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
